import { useMemo, useState } from 'react'

import { Input } from '@ui/components/core'
import { useManagePipeline } from '@ui/components/elements/ManageAccess/hooks/useManagePipeline'
import { VerticalMenuIcon } from '@ui/components/icons'
import { Spinner } from '@ui/components/loaders'
import { Member } from '@ui/interfaces'
import { Team } from '@ui/services'

interface ManageAccessProps {
  teams: Team[]
  members: Member[]
  currentMember: Member
  pipelineId: string
}

const getMemberName = (member: Member) => `${member.first_name} ${member.last_name}`

export const ManageAccess = ({ teams, members, currentMember, pipelineId }: ManageAccessProps) => {
  const [search, setSearch] = useState('')
  const [menuOpenFor, setMenuOpenFor] = useState('')

  const { invitedTeams, inviteTeam, inviteUser, invitedUsers, removeUser, removeTeam, isPipelineLoading } =
    useManagePipeline(pipelineId, teams, members, currentMember)

  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      const isAlreadyInvited = invitedTeams.some((invitedTeam) => invitedTeam.id === team.id)
      const matchesSearchTerm = team.name.toLowerCase().includes(search.toLowerCase())
      return !isAlreadyInvited && matchesSearchTerm
    })
  }, [teams, invitedTeams, search])

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const isSelf = member.id === currentMember.id
      const isAlreadyInvited = invitedUsers.some((invitedUser) => invitedUser.id === member.id)
      const matchesSearchTerm = [getMemberName(member), member.email].some((name) =>
        name.toLowerCase().includes(search.toLowerCase())
      )

      return !isSelf && !isAlreadyInvited && matchesSearchTerm
    })
  }, [members, invitedUsers, search])

  return (
    <div className="flex flex-col space-y-2 p-4">
      {isPipelineLoading ? (
        <Spinner testId="spinner" />
      ) : (
        <>
          <div className="relative">
            <div className="flex flex-row items-end justify-between space-x-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                label="Team Name or Individual Name"
                placeholder="Search"
              />
            </div>

            {search.length > 0 && (
              <div className="absolute left-0 top-14 z-20 h-40 w-full overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
                {filteredTeams.map((team) => (
                  <div
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      inviteTeam.mutate(team.id)
                      setSearch('')
                    }}
                    key={team.name}>
                    <p className="text-sm">{team.name}</p>
                  </div>
                ))}
                {filteredMembers.map((member) => (
                  <div
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      inviteUser.mutate(member.id)
                      setSearch('')
                    }}
                    key={member.id}>
                    <p className="text-sm">{getMemberName(member)}</p>
                    <p className="text-sm">{member.email}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <h2 className="text-sm font-semibold">Members</h2>
          <div className="flex max-h-40 flex-col space-y-2 overflow-y-auto">
            {invitedUsers.length === 0 ? (
              <div className="text-sm text-gray-500">No members invited</div>
            ) : (
              invitedUsers.map((member) => (
                <div className="flex w-full flex-row justify-between" key={member.id}>
                  <p className="text-sm">{getMemberName(member)}</p>
                  <div className="relative flex flex-row items-center space-x-2 py-2">
                    <VerticalMenuIcon
                      className="size-4 cursor-pointer stroke-gray-900"
                      onClick={() => setMenuOpenFor(`user-${member.id}`)}
                    />
                    {menuOpenFor === `user-${member.id}` && (
                      <div className="absolute right-4 z-10 rounded-md border border-gray-200 bg-white shadow-md">
                        <div
                          className="px-2 py-1 hover:cursor-pointer hover:bg-gray-100"
                          onClick={() => {
                            setMenuOpenFor('')
                            removeUser.mutate(member.userPermission.id)
                          }}>
                          <p className="text-xs font-normal hover:cursor-pointer hover:text-gray-900">Remove</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <h2 className="text-sm font-semibold">Teams</h2>
          <div className="flex flex-col space-y-2">
            {invitedTeams.length === 0 ? (
              <div className="text-sm text-gray-500">No teams invited</div>
            ) : (
              invitedTeams.map((team) => (
                <div className="flex w-full flex-row justify-between" key={team.id}>
                  <p className="text-sm">{team.name}</p>
                  <div className="relative flex flex-row items-center space-x-2 py-2">
                    <VerticalMenuIcon
                      className="size-4 cursor-pointer stroke-gray-900"
                      onClick={() => setMenuOpenFor(`team-${team.id}`)}
                    />
                    {menuOpenFor === `team-${team.id}` && (
                      <div className="absolute right-4 z-10 rounded-md border border-gray-200 bg-white shadow-md">
                        <div
                          className="px-2 py-1 hover:cursor-pointer hover:bg-gray-100"
                          onClick={() => {
                            setMenuOpenFor('')
                            removeTeam.mutate(team.teamPermission.id)
                          }}>
                          <p className="text-xs font-normal hover:cursor-pointer hover:text-gray-900">Remove</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
